import {Injectable} from '@angular/core';
import * as _ from 'lodash';

enum AttributeType {
  prefix = 'PREFIX',
  host = 'HOST_NAME'
}

enum TopologyElements {
  offlineVoipPeer = 'Offline VoIP Peer',
  onlineVoipPeer = 'Online VoIP Peer',
  offlineNode = 'Offline Node',
  onlineNode=  'Online Node',
  offlinePeerConnection = 'Offline Peer Connection',
  onlinePeerConnection = 'Online Peer Connection',
  resourceGroupNode = 'Nodes group',
  resourceGroupPeerConnection = 'Peer Connections group',
  resourceGroupVoipPeer = 'VoIP Peers group'
}

@Injectable({ providedIn: 'root' })
export class PrepareRuleDataService {

  constructor() {

  }

  convertRuleFromServerToDialog(resultObject, providedRule, optionLists): any {
    return this.convertRuleFromServerToDialogHelper(resultObject, providedRule, optionLists);
  }

  convertRuleFromDialogToServer(rule): any {
    return this.convertRuleFromDialogToServerHelper(rule);
  }

  private convertRuleFromServerToDialogHelper(resultObject, providedRule, optionLists): void {
    if (_.values(providedRule).some(x => x !== undefined)) {
      this.restructureRuleParameters(resultObject, providedRule, optionLists);
    } else {
      this.buildFromScratch(resultObject);
    }
  }

  private buildFromScratch(resultObject): void {
    resultObject.name = '';
    resultObject.sipHeaders = [];
    resultObject.tags = [];
    resultObject.adminState = 'LOCKED';
    resultObject.adminStateBoolean = true;
    resultObject.testMode = 'UNLOCKED';
    resultObject.testModeBoolean = false;
    resultObject.source = {
      rrAttrs: { host: [], prefix: [] },
      nodes: [],
      pCons: [],
      reroutePcons: [],
      userGroups: [],
      resourceGroups: []
    };

    resultObject.destination = {
      rrAttrs: { host: [], prefix: [] },
      userGroups: []
    };

    resultObject.action = {
      discard: false,
      sipReason: 1,
      actions: [],
      continueWithNodeInternalTables: false
    };

    resultObject.quality = null;

    resultObject.eventOnMatch = false;

    resultObject.tbrConditionsIds = null;

    resultObject.isEmergency = false;

    resultObject.isRegisteredUsers = false;

    resultObject.isTrigger3xx = true;
    resultObject.isTriggerRefer = true;
    resultObject.isTriggerInitialOnly = true;
    resultObject.isTriggerBrokenConnection = true;
    resultObject.isTriggerFaxRerouting = true;
    resultObject.isPreventSourceLoopbackNode = false;
    resultObject.isPreventSourceLoopbackPcon = false;
    resultObject.isPreventSourceLoopbackVoipPeer = false;
    resultObject.privacy = 'TRANSPARENT';
    resultObject.routingMethod = 'seq';
    resultObject.noAnswerTimeout = 600;
    resultObject.isNoAnswerTimeoutEnable = false;
    resultObject.minSecureLogix = null;
    resultObject.maxSecureLogix = null;
    resultObject.isActionDirective = false;
    resultObject.requestType = 'CALL';
  }

  private restructureRuleParameters(resultObject, rule, optionLists): void {
    resultObject.name = rule.name;
    resultObject.adminState = rule.adminState;
    resultObject.adminStateBoolean = _.isEqual(rule.adminState, 'LOCKED');
    resultObject.testMode = rule.testMode;
    resultObject.testModeBoolean = _.isEqual(rule.testMode, 'LOCKED');
    resultObject.quality = rule.quality;
    resultObject.tbrConditionsIds = rule.tbrConditionsIds;
    resultObject.isEmergency = rule.isEmergency;
    resultObject.isRegisteredUsers = rule.isRegisteredUsers;
    resultObject.eventOnMatch = rule.eventOnMatch;
    resultObject.requestType = rule.requestType;
    resultObject.isTrigger3xx = rule.isTrigger3xx;
    resultObject.isTriggerRefer = rule.isTriggerRefer;
    resultObject.isTriggerInitialOnly = rule.isTriggerInitialOnly;
    resultObject.isTriggerBrokenConnection = rule.isTriggerBrokenConnection;
    resultObject.isTriggerFaxRerouting = rule.isTriggerFaxRerouting;
    resultObject.isPreventSourceLoopbackPcon = rule.isPreventSourceLoopbackPcon;
    resultObject.isPreventSourceLoopbackVoipPeer = rule.isPreventSourceLoopbackVoipPeer;
    resultObject.isPreventSourceLoopbackNode = rule.isPreventSourceLoopbackNode;
    resultObject.privacy = rule.privacy;
    resultObject.routingMethod = rule.routingMethod;
    resultObject.isNoAnswerTimeoutEnable = !_.isNil(rule.noAnswerTimeout);
    resultObject.noAnswerTimeout = rule.noAnswerTimeout ? rule.noAnswerTimeout : 600;
    resultObject.minSecureLogix = rule.minSecureLogix;
    resultObject.maxSecureLogix = rule.maxSecureLogix;
    resultObject.isActionDirective = rule.isActionDirective;
    resultObject.isActionDirectiveAllow = rule.isActionDirectiveAllow;
    resultObject.isActionDirectiveBlock = rule.isActionDirectiveBlock;
    resultObject.isActionDirectiveRedirect = rule.isActionDirectiveRedirect;
    resultObject.sipConditionGroupId = rule.sipConditionGroupId;
    this.manipulateSipHeaders(resultObject, rule);
    this.manipulateTags(resultObject, rule);
    this.manipulateSource(resultObject, rule, optionLists);
    this.manipulateDestination(resultObject, rule, optionLists);
    this.manipulateAction(resultObject, rule, optionLists);
  }

  private manipulateSipHeaders(resultObject, ruleObject): void {
    resultObject.sipHeaders = [];
    const sipHeadersObject = {};
    if (ruleObject.sipHeaders) {
      ruleObject.sipHeaders.forEach((sipHeader) => {
        if (sipHeadersObject[sipHeader.name]) {
          sipHeadersObject[sipHeader.name].push(sipHeader.value);
        } else {
          sipHeadersObject[sipHeader.name] = [sipHeader.value];
        }
      });

      for (const entry of _.toPairs(sipHeadersObject)) {
        resultObject.sipHeaders.push({name: entry[0], value: entry[1]});
      }
    }
  }

  private manipulateTags(resultObject, ruleObject): void {
    resultObject.tags = [];
    const tagsObject = {};
    if (ruleObject.tags) {
      ruleObject.tags.forEach((tag) => {
        if (tagsObject[tag.tag]) {
          tagsObject[tag.tag].push(tag.value);
        } else {
          tagsObject[tag.tag] = [tag.value];
        }
      });

      for (const entry of _.toPairs(tagsObject)) {
        resultObject.tags.push({tag: entry[0], value: entry[1]});
      }
    }
  }

  private manipulateSource(resultObject, ruleObject, optionLists): void {
    resultObject.source = {};

    resultObject.source.nodes = ruleObject.sourceNodes;
    resultObject.source.pCons = ruleObject.sourcePcons;
    resultObject.source.reroutePcons = ruleObject.sourceReroutePcons;
    resultObject.source.userGroups = ruleObject.sourceUserGroups;
    resultObject.source.resourceGroups = ruleObject.sourceTopologyGroups;
    resultObject.source.allCustomers = ruleObject.allSourceCustomers;
    resultObject.source.sourceCustomers = ruleObject.sourceCustomers;

    this.populateRRAttributes(ruleObject.sourceRRAttr, resultObject.source, optionLists);
  }

  private manipulateDestination(resultObject, ruleObject, optionLists): void {
    resultObject.destination = {};
    resultObject.destination.userGroups = ruleObject.dstUserGroups;
    resultObject.destination.allCustomers = ruleObject.allDestCustomers;
    resultObject.destination.destCustomers = ruleObject.destCustomers;

    this.populateRRAttributes(ruleObject.destRRAttr, resultObject.destination, optionLists);
  }

  private manipulateAction(resultObject, actionObject, optionLists): void {
    resultObject.action = { actions: [] };
    let index = 0;

    const mergedActions = this.mergedActionsByPriority(actionObject.actions);

    for (const item in mergedActions) {
      if (mergedActions.hasOwnProperty(item)) {
        const tmpActionItem = this.buildActionItem(mergedActions[item], index, optionLists);
        resultObject.action.actions.push(tmpActionItem);
        index++;
      }
    }
  }


  private populateRRAttributes(rrAttributesObj, resultObject, optionLists): void {
    resultObject.rrAttrs = { host: [], prefix: [] };

    this.populatePrefixesAndHosts(resultObject.rrAttrs, rrAttributesObj.attributes);
    this.populatePrefixGroups(resultObject.rrAttrs, rrAttributesObj.attributeGroups, optionLists);
  }

  private populatePrefixesAndHosts(resultObject, rrAttributes): void {
    if (_.isArray(rrAttributes) && rrAttributes.length > 0) {
      const attributesCount = rrAttributes.length;

      for (let i = 0; i < attributesCount; i++) {
        if (rrAttributes[i].type === AttributeType.prefix) {
          resultObject.prefix.push({ customTag: true, name: rrAttributes[i].value});
        } else {
          resultObject.host.push(rrAttributes[i].value);
        }
      }
    }
  }

  private populatePrefixGroups(resultObject, prefixGroups, optionLists): void {
    if (_.isArray(prefixGroups) && prefixGroups.length > 0) {
      const prefixCount = prefixGroups.length;

      for (let i = 0; i < prefixCount; i++) {
        resultObject.prefix.push(this.findObjectFromId(prefixGroups[i], optionLists.prefixGroups));
      }
    }
  }

  private mergedActionsByPriority(actions): any {
    const copiedActions = _.cloneDeep(actions);
    const copyLength = copiedActions.length;
    const result = {};
    let i = 0;

    for (i = 0; i < copyLength; i++) {
      if (!(copiedActions[i].priority in result)) {
        result[copiedActions[i].priority] = [];
      }
    }

    for (i = 0; i < copyLength; i++) {
      result[copiedActions[i].priority].push(copiedActions[i]);
    }

    return result;
  }

  private buildActionItem(actionItem, index, optionLists): any {
    const newActionObject: any = {
      id: index,
      actions: [],
      isEquallyBalanced: actionItem[0].isEquallyBalanced
    } ;
    const actionItemCount = actionItem.length;

    for (let i = 0; i < actionItemCount; i++) {
      newActionObject.actions.push({
        id: actionItem[i].id,
        percentage: actionItem[i].weight,
        discard: actionItem[i].discard,
        loopback: actionItem[i].loopback,
        continueWithNodeInternalTables: actionItem[i].continueWithNodeInternalTables,
        registeredUsers: actionItem[i].registeredUsers,
        sipReason: !_.isNil(actionItem[i].sipReason) ? actionItem[i].sipReason : 1,
        isSipReasonDefined: actionItem[i].sipReason ? true : false,
        routingAttempts: actionItem[i].routingAttempts,
        isEquallyBalanced: actionItem[i].isEquallyBalanced,
        isNormalizationCollapsed : true,
        srcNormalizationGroup: actionItem[i].srcNormalizationGroupId,
        destNormalizationGroup: actionItem[i].destNormalizationGroupId,
        sipManipulationGroupId: actionItem[i].sipManipulationGroupId,
        destTypeRequestUri: actionItem[i].destTypeRequestUri,
        customerNormalizationSIPHeader: actionItem[i].customerNormalizationSIPHeader,
        srcNormalizationFromHeader: actionItem[i].srcNormalizationFromHeader,
        srcNormalizationPAIHeader: actionItem[i].srcNormalizationPAIHeader,
        srcNormalizationPPIHeader: actionItem[i].srcNormalizationPPIHeader,
        combined: this.getCombinedSelection(actionItem[i].nodeId, actionItem[i].peerConnectionId,
          actionItem[i].voipPeerId, actionItem[i].topologyGroupId, optionLists),
        viaNodes: actionItem[i].viaNodes && actionItem[i].viaNodes.length > 0 ? actionItem[i].viaNodes[0] : null,
        attemptsInTopologyGroup: actionItem[i].attemptsInTopologyGroup
      });
    }

    newActionObject.title = this.generateNewActionTitle(actionItem[actionItemCount - 1], newActionObject);

    return newActionObject;
  }

  private generateNewActionTitle(actionItem, newActionObject): string {
    if (actionItem.discard) {
      return 'Discard Action';
    } else if (actionItem.continueWithNodeInternalTables) {
      return 'Continue with Node`s internal routing table';
    } else if (actionItem.registeredUsers) {
      return 'Registered users action';
    } else if (actionItem.loopback) {
      return 'Loopback action';
    }

    return `[${newActionObject.actions[0].combined.elementType}] ${newActionObject.actions[0].combined.name}`;
  }

  private findObjectFromId(id, objectsArray): any {
    return objectsArray.find((item) => {
      return item.id === id;
    }) || { id: null, name: null };
  }

  private getCombinedSelection(node, peerConnection, voipPeer, resourceGroup, optionLists): any {
    if (voipPeer) {
      return this.findObjectFromId(voipPeer, optionLists.voipPeers.concat(optionLists.offlineVoipPeers));
    } else if (peerConnection) {
      return this.findObjectFromId(peerConnection, optionLists.pCons.concat(optionLists.offlinePCons));
    }else if (node) {
      return this.findObjectFromId(node, optionLists.nodes.concat(optionLists.offlineNodes));
    } else if (resourceGroup) {
      return this.findObjectFromId(resourceGroup, optionLists.resourceGroups);
    }
  }


  // HELPER FUNCTION TO CONVERT TO SERVER
  private convertRuleFromDialogToServerHelper(rule): any {
    const mappedRule = {
      name: rule.name,
      testMode: rule.testMode,
      adminState: rule.adminState,
      quality: rule.quality,
      tbrConditionsIds: rule.tbrConditionsIds,
      isEmergency: rule.isEmergency,
      isRegisteredUsers: rule.isRegisteredUsers,
      eventOnMatch: rule.eventOnMatch,
      isTrigger3xx: rule.isTrigger3xx,
      isTriggerRefer: rule.isTriggerRefer,
      isTriggerInitialOnly: rule.isTriggerInitialOnly,
      isTriggerBrokenConnection: rule.isTriggerBrokenConnection,
      isTriggerFaxRerouting: rule.isTriggerFaxRerouting,
      isPreventSourceLoopbackPcon: rule.isPreventSourceLoopbackPcon,
      isPreventSourceLoopbackVoipPeer: rule.isPreventSourceLoopbackVoipPeer,
      isPreventSourceLoopbackNode: rule.isPreventSourceLoopbackNode,
      privacy: rule.privacy,
      routingMethod: rule.routingMethod,
      noAnswerTimeout: rule.isNoAnswerTimeoutEnable ? rule.noAnswerTimeout : null,
      sourceNodes: rule.source.nodes,
      sourcePcons: rule.source.pCons,
      sourceReroutePcons: rule.source.reroutePcons,
      sourceTopologyGroups: rule.source.resourceGroups,
      sourceUserGroups: rule.source.userGroups,
      dstUserGroups: rule.destination.userGroups,
      minSecureLogix: rule.isSecureLogixEnabled ? rule.minSecureLogix : null,
      maxSecureLogix: rule.isSecureLogixEnabled ? rule.maxSecureLogix : null,
      isActionDirective: rule.isActionDirective,
      isActionDirectiveAllow: rule.isActionDirectiveAllow,
      isActionDirectiveBlock: rule.isActionDirectiveBlock,
      isActionDirectiveRedirect: rule.isActionDirectiveRedirect,
      requestType: rule.requestType,
      allSourceCustomers: rule.source.allCustomers,
      allDestCustomers: rule.destination.allCustomers,
      sourceCustomers: rule.source.allCustomers ? [] : rule.source.sourceCustomers,
      destCustomers: rule.destination.allCustomers ? [] : rule.destination.destCustomers,
      sipConditionGroupId: rule.sipConditionGroupId,
      sipManipulationGroupId: rule.sipManipulationGroupId
    };

    this.restoreSipHeaders(mappedRule, rule.sipHeaders);
    this.restoreTags(mappedRule, rule.tags);
    this.restoreRRAttributes(mappedRule, rule.source.rrAttrs, 'sourceRRAttr');
    this.restoreRRAttributes(mappedRule, rule.destination.rrAttrs, 'destRRAttr');

    this.restoreActions(mappedRule, rule.action);

    return mappedRule;
  }

  private restoreSipHeaders(restoreTo, sipHeaders): void {
    restoreTo.sipHeaders = [];
    sipHeaders.forEach((sipHeader) => {
      sipHeader.value.forEach((value) => {
        restoreTo.sipHeaders.push({name: sipHeader.name, value});
      });
    });
  }

  private restoreTags(restoreTo, tags): void {
    restoreTo.tags = [];
    tags.forEach((tag) => {
      tag.value.forEach((value) => {
        restoreTo.tags.push({tag: tag.tag, value});
      });
    });
  }

  private restoreRRAttributes(restoreTo, restoreFrom, restoreMode): void {
    restoreTo[restoreMode] = { attributes: [], attributeGroups: [] };

    restoreFrom.host.forEach((host) => {
      restoreTo[restoreMode].attributes.push({ type: AttributeType.host, value: host });
    });

    restoreFrom.prefix.forEach((prefix) => {
      if (prefix.customTag === true) {
        restoreTo[restoreMode].attributes.push({ type: AttributeType.prefix, value: prefix.name });
      } else {
        restoreTo[restoreMode].attributeGroups.push(prefix.id);
      }
    });
  }

  private restoreActions(restoreTo, restoreFrom): void {
    restoreTo.actions = [];

    let priority = 1;
    restoreFrom.actions.forEach((action) => {
      this.buildActionElement(action.actions, restoreTo.actions, priority);
      priority++;
    });
  }

  private buildActionElement(actionItem, resultObject, priority): void {
    actionItem.forEach((value) => {
      const newItem: any = {
        id: value.id ? value.id : null,
        priority,
        weight: value.percentage,
        isEquallyBalanced: value.isEquallyBalanced,
        routingAttempts: value.routingAttempts,
        srcNormalizationGroupId: value.srcNormalizationGroup,
        destNormalizationGroupId: value.destNormalizationGroup,
        sipManipulationGroupId: value.sipManipulationGroupId,
        destTypeRequestUri: value.destTypeRequestUri,
        customerNormalizationSIPHeader: value.customerNormalizationSIPHeader,
        srcNormalizationFromHeader: value.srcNormalizationFromHeader,
        srcNormalizationPAIHeader: value.srcNormalizationPAIHeader,
        srcNormalizationPPIHeader: value.srcNormalizationPPIHeader,
        discard: value.discard,
        loopback: value.loopback,
        continueWithNodeInternalTables: value.continueWithNodeInternalTables,
        registeredUsers: value.registeredUsers,
        sipReason: value.isSipReasonDefined ? value.sipReason : null,
        viaNodes: value.viaNodes ? [value.viaNodes] : null,
        attemptsInTopologyGroup: value.attemptsInTopologyGroup ? value.attemptsInTopologyGroup : 1
      };

      if (!value.discard && !value.continueWithNodeInternalTables && !value.registeredUsers && !value.loopback) {
        this.extractCombined(value.combined, newItem);
        if (newItem.voipPeerId) {
          newItem.viaNodes = null;
        }
      }
      resultObject.push(newItem);
    });
  }

  private extractCombined(combined, resultObj): void {

    switch (combined.elementType) {
      case TopologyElements.offlineVoipPeer:
      case TopologyElements.onlineVoipPeer:
        resultObj.voipPeerId = combined.id;
        break;
      case TopologyElements.offlineNode:
      case TopologyElements.onlineNode:
        resultObj.nodeId = combined.id;
        break;
      case TopologyElements.offlinePeerConnection:
      case TopologyElements.onlinePeerConnection:
        resultObj.peerConnectionId = combined.id;
        resultObj.nodeId = combined.nodeId;
        break;
      case TopologyElements.resourceGroupNode:
      case TopologyElements.resourceGroupPeerConnection:
      case TopologyElements.resourceGroupVoipPeer:
        resultObj.topologyGroupId = combined.id;
        break;
    }
  }
}
